import { useEffect, useMemo, useState } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Radio, RefreshCw, CheckCircle2, XCircle } from "lucide-react";
import { cn } from "@/lib/utils";
import { supabase } from "@/integrations/supabase/client";

interface StreamRow {
  id: string;
  name: string;
  description: string;
  video_id: string;
  is_active: boolean | null;
  last_checked_at: string | null;
}

function formatDate(dt?: string | null) {
  if (!dt) return "—";
  try {
    const d = new Date(dt);
    return d.toLocaleString();
  } catch {
    return "—";
  }
}

function getNextVerification(now = new Date()) {
  // assuming cron runs every 15 minutes: */15 * * * *
  const minutes = now.getMinutes();
  const add = 15 - (minutes % 15 || 15);
  const next = new Date(now.getTime() + add * 60_000);
  next.setSeconds(0, 0);
  return next;
}

export default function YouTubeStreamsStatus() {
  const [streams, setStreams] = useState<StreamRow[]>([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    const { data, error } = await (supabase as any)
      .from('youtube_streams')
      .select('*')
      .order('id');
    if (!error && data) {
      setStreams(
        data.map((s: any) => ({
          id: s.id,
          name: s.name,
          description: s.description,
          video_id: s.video_id,
          is_active: s.is_active,
          last_checked_at: s.last_checked_at,
        }))
      );
    }
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  const lastVerified = useMemo(() => {
    const latest = streams
      .map((s) => (s.last_checked_at ? new Date(s.last_checked_at).getTime() : 0))
      .reduce((a, b) => Math.max(a, b), 0);
    return latest ? new Date(latest) : null;
  }, [streams]);

  const nextVerification = useMemo(() => getNextVerification(), []);

  const handleSelect = (stream: StreamRow) => {
    window.dispatchEvent(new CustomEvent('yt-radio-select-stream', { detail: { id: stream.id } }));
    window.dispatchEvent(new Event('yt-radio-play'));
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div className="text-sm text-muted-foreground">
          <div>Last verified: {lastVerified ? lastVerified.toLocaleString() : '—'}</div>
          <div>Next verification: {nextVerification.toLocaleTimeString()}</div>
        </div>
        <Button
          onClick={load}
          disabled={loading}
          variant="ghost"
          size="sm"
          className="h-8"
        >
          <RefreshCw className={cn("h-4 w-4", loading && "animate-spin")} />
        </Button>
      </div>

      <div className="space-y-2 overflow-y-auto pr-2">
        {streams.map((s) => (
          <Card
            key={s.id}
            className={cn(
              "p-3 cursor-pointer transition-all",
              !s.is_active 
                ? 'opacity-60' 
                : 'hover:bg-accent/50 hover:shadow-lg'
            )}
            onClick={() => handleSelect(s)}
          >
            <div className="flex items-start justify-between gap-3">
              <div className="flex items-start gap-3 flex-1 min-w-0">
                {/* YouTube Thumbnail */}
                <div className="relative flex-shrink-0">
                  <img 
                    src={`https://img.youtube.com/vi/${s.video_id}/mqdefault.jpg`}
                    alt={s.name}
                    className="h-12 w-20 rounded-md object-cover"
                    onError={(e) => {
                      e.currentTarget.src = `https://img.youtube.com/vi/${s.video_id}/default.jpg`;
                    }}
                  />
                  {/* Status indicator overlay */}
                  <div className={cn(
                    "absolute -top-1 -right-1 h-3 w-3 rounded-full border-2 border-background",
                    s.is_active ? "bg-green-500" : "bg-red-500"
                  )} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1 flex-wrap">
                    <h4 className="font-semibold text-sm truncate">{s.name}</h4>
                    <Badge 
                      variant={s.is_active ? "default" : "destructive"}
                      className="text-xs"
                    >
                      {s.is_active ? (
                        <span className="flex items-center gap-1">
                          <CheckCircle2 className="h-3 w-3" />
                          Online
                        </span>
                      ) : (
                        <span className="flex items-center gap-1">
                          <XCircle className="h-3 w-3" />
                          Offline
                        </span>
                      )}
                    </Badge>
                  </div>
                  <p className="text-xs text-muted-foreground line-clamp-2">{s.description}</p>
                </div>
              </div>
            </div>
          </Card>
        ))}
      </div>
    </div>
  );
}
